import React, { useEffect, useState } from "react";

import { useTrainerGroupsOptions, useTrainers } from "api/hooks";
import { AdminResourceDeleteModal, AdminSearchInput, FiltersBackground, PageMain } from "components/molecules";
import { useNavigate } from "react-router-dom";
import { routes } from "consts";
import { IDropdownOption } from "components/atoms";
import { MoreActionsMenu, MultiSelect, PaziaButton, Select } from "components/atoms";
import {
  TanTable,
  useFilters,
  useInitFilters,
  usePagination,
  useSorting,
  TanTableColumn,
} from "components/molecules";
import { trainersApi } from "api";
import { TrainerApi } from "api/types";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { useDeleteResourceActions } from "utils/store";
import { BulkGenerateTrainerAccountModal } from "./BulkGenerateTrainerAccountModal";
import { BulkSendTrainerInviteModal } from "./BulkSendTrainerInviteModal";

const HAS_ACCOUNT_OPTIONS: IDropdownOption[] = [
  { value: "true", label: "Z kontem" },
  { value: "false", label: "Bez konta" },
];

export const TrainersPage = () => {
  const { setDeleteResource } = useDeleteResourceActions();
  const { searchPhrase, filter, setSearchPhrase, changeFilter, getInitFilterValues } = useFilters("trainers");
  const { initFilters } = useInitFilters("trainers");
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting();

  const { isLoading, isFetching, trainers, total, invalidate } = useTrainers(
    skip,
    limit,
    searchPhrase,
    filter,
    fieldWithOrder,
  );
  const navigate = useNavigate();
  const { loading: loadingGroups, options: groupOptions } = useTrainerGroupsOptions();

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [tableKey, setTableKey] = useState(0);
  const [bulkGenerateAccountModalOpen, setBulkGenerateAccountModalOpen] = useState(false);
  const [bulkSendInviteModalOpen, setBulkSendInviteModalOpen] = useState(false);
  const [hasAccountValue, setHasAccountValue] = useState<string>(() => {
    const initHasAccount = initFilters["hasAccount"] ?? "";
    if (initHasAccount.includes("eq true")) return "true";
    if (initHasAccount.includes("eq false")) return "false";
    return "";
  });

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase, filter]);
  const columns = React.useMemo<TanTableColumn<TrainerApi>[]>(() => cols, []);

  const onGroupsChange = (types: IDropdownOption[]) => {
    if (types.length < 1) return changeFilter("groups", "");
    const typesIds = types.map((x) => `'${x.value}'`).join(",");
    changeFilter("groups", `groups in (${typesIds})`);
  };

  const onHasAccountChange = (opt: IDropdownOption | null) => {
    const value = opt ? String(opt.value) : "";
    setHasAccountValue(value);
    changeFilter("hasAccount", value ? `hasAccount eq ${value}` : "");
  };

  const handleBulkSuccess = () => {
    setSelectedRows([]);
    setTableKey((prev) => prev + 1);
    invalidate();
  };

  return (
    <PageMain>
      <PageRichHeader header="Trenerzy">
        <div className="flex grow flex-wrap justify-between gap-2">
          <div className="flex gap-2">
            <PaziaButton
              type="primary"
              title="Dodaj +"
              className="whitespace-nowrap"
              onClick={() => navigate(routes.TRAINERS_ADD)}
            />
            <MoreActionsMenu
              actions={[
                { name: "Utwórz konto", onClick: () => setBulkGenerateAccountModalOpen(true) },
                { name: "Wyślij zaproszenie do panelu", onClick: () => setBulkSendInviteModalOpen(true) },
              ]}
              disabled={selectedRows.length < 1}
            >
              {`Akcje (${selectedRows.length})`}
            </MoreActionsMenu>
          </div>
          <div className="">
            <PaziaButton
              type="secondary"
              title="Grupy trenerów"
              className="whitespace-nowrap"
              onClick={() => navigate(routes.TRAINERS_GROUPS)}
            />
          </div>
        </div>
      </PageRichHeader>

      <FiltersBackground className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <AdminSearchInput onSearch={(searchPhrase: string) => setSearchPhrase(searchPhrase)} className="self-end" />

          <MultiSelect
            label="Grupa"
            name="groups"
            options={groupOptions}
            disabled={loadingGroups}
            defaultValue={getInitFilterValues("groups")}
            onChange={onGroupsChange}
            className="grow"
          />

          <Select
            label="Konto"
            name="hasAccount"
            options={HAS_ACCOUNT_OPTIONS}
            value={hasAccountValue}
            onChange={onHasAccountChange}
            placeholder="Wszyscy"
            className="grow"
          />
        </div>
        <div className="grid grid-cols-2 gap-2"></div>
      </FiltersBackground>
      <TanTable
        key={tableKey}
        data={trainers}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        onDelete={(x) => setDeleteResource(x.id)}
        pagination={pagination}
        sorting={sorting}
        onEdit={(x) => navigate(routes.TRAINERS_EDIT(x.id))}
        onRowClick={(x) => navigate(routes.TRAINER(x.id))}
        onSelectionChange={setSelectedRows}
      />
      <AdminResourceDeleteModal
        title="Usuń trenera"
        description="Jesteś pewien że chcesz usunąć trenera?"
        onSuccessDelete={() => invalidate()}
        deleteAction={trainersApi.delete}
      />
      <BulkGenerateTrainerAccountModal
        isOpen={bulkGenerateAccountModalOpen}
        onCloseRequest={() => setBulkGenerateAccountModalOpen(false)}
        selectedTrainerIds={selectedRows}
        onSuccess={handleBulkSuccess}
      />
      <BulkSendTrainerInviteModal
        isOpen={bulkSendInviteModalOpen}
        onCloseRequest={() => setBulkSendInviteModalOpen(false)}
        selectedTrainerIds={selectedRows}
        onSuccess={handleBulkSuccess}
      />
    </PageMain>
  );
};

const cols: TanTableColumn<TrainerApi>[] = [
  {
    id: "id",
    hideOnMobile: true,
    accessorKey: "id",
    header: "ID",
    enableColumnFilter: false,
    size: 10,
  },
  {
    id: "fullName",
    accessorKey: "firstName",
    header: "Imię i nazwisko",
    size: 250,
    enableColumnFilter: false,
    cell: (info) => `${info.row.original.firstName} ${info.row.original.surname}`,
  },
  {
    id: "email",
    tdClassName: "max-md:break-all",
    accessorKey: "email",
    header: "Email",
    enableColumnFilter: false,
  },
  {
    id: "groups",
    hideOnMobile: true,
    accessorKey: "groups",
    header: "Grupy",
    enableSorting: false,
    enableColumnFilter: false,
    size: 700,
    cell: (info) => (info.row.original.groups ?? []).map((x) => x.name).join(", "),
  },
  {
    id: "hasAccount",
    hideOnMobile: true,
    accessorKey: "userId",
    header: "Konto",
    enableSorting: false,
    enableColumnFilter: false,
    size: 100,
    cell: (info) => (
      <span
        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
          info.row.original.userId
            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
            : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
        }`}
      >
        {info.row.original.userId ? "Tak" : "Nie"}
      </span>
    ),
  },
];
